import { useQueryState } from "nuqs";
import { useCallback, useEffect, useMemo, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { toast } from "@volca/ui/lib/toast";
import { useGetBusinesses } from "@/business/query";
import { useGetMyUser } from "@/user/query";
import BusinessContext from "./business-context";
import { BUSINESS_PARAM } from "./business-param";
import {
  getBusinessSelectableBusinesses,
  getTenantSelectableBusinesses,
  getValidTenantBusinessId,
} from "./utils";

const STORAGE_KEY = "selectedBusinessId";

const BusinessProvider: React.FC<{ children: React.ReactNode }> = ({
  children,
}) => {
  const navigate = useNavigate();
  const location = useLocation();

  // Fetch user and businesses
  const {
    data: user,
    isLoading: isUserLoading,
    isError: isUserError,
  } = useGetMyUser();
  const {
    data: allBusinesses = [],
    isLoading: isBusinessesLoading,
    isError: isBusinessesError,
  } = useGetBusinesses();

  // State for selected business (only relevant for tenant users)
  const [storedBusinessId, setStoredBusinessId] = useState<string | null>(() =>
    localStorage.getItem(STORAGE_KEY),
  );

  // Determine if user is a tenant user
  const isTenantUser = user?.parentable_type === "Tenant";

  const [urlBusinessId, setUrlBusinessId] = useQueryState(BUSINESS_PARAM, {
    history: "replace",
    shallow: false,
  });
  const [appliedUrlBusinessId, setAppliedUrlBusinessId] = useState<
    string | null
  >(null);

  const businesses = useMemo(
    () =>
      isTenantUser
        ? getTenantSelectableBusinesses(allBusinesses)
        : getBusinessSelectableBusinesses(allBusinesses, user?.parentable_id),
    [allBusinesses, isTenantUser, user?.parentable_id],
  );

  // Select a business and update URL if needed
  const selectBusiness = useCallback(
    (businessId: string) => {
      if (!businesses.some((business) => business.id === businessId)) return;
      setStoredBusinessId(businessId);
      localStorage.setItem(STORAGE_KEY, businessId);
      if (urlBusinessId) {
        setAppliedUrlBusinessId(businessId);
        if (urlBusinessId !== businessId) void setUrlBusinessId(businessId);
      }

      const settingsPath = location.pathname.replace(
        /\/business-settings\/[^/]+/,
        `/business-settings/${businessId}`,
      );
      if (settingsPath !== location.pathname) {
        navigate(settingsPath + location.search);
      }
    },
    [businesses, navigate, location, urlBusinessId, setUrlBusinessId],
  );

  useEffect(() => {
    if (!urlBusinessId) setAppliedUrlBusinessId(null);
  }, [urlBusinessId]);

  const linkedBusinessId =
    isTenantUser &&
    urlBusinessId &&
    urlBusinessId !== appliedUrlBusinessId &&
    businesses.some((business) => business.id === urlBusinessId)
      ? urlBusinessId
      : null;

  const selectedBusinessId = isTenantUser
    ? getValidTenantBusinessId(businesses, linkedBusinessId ?? storedBusinessId)
    : null;

  // Determine the effective business ID
  // - For tenant users: use the selected business
  // - For business users: use their parentable_id
  const businessId = isTenantUser
    ? selectedBusinessId
    : user?.parentable_id || null;

  const selectedBusiness = businesses.find((b) => b.id === businessId);

  // Store only a permitted tenant selection. Business accounts have no saved choice.
  useEffect(() => {
    if (!user) return;
    if (isTenantUser && (isBusinessesLoading || isBusinessesError)) return;

    if (selectedBusinessId !== storedBusinessId) {
      setStoredBusinessId(selectedBusinessId);
      if (selectedBusinessId)
        localStorage.setItem(STORAGE_KEY, selectedBusinessId);
      else localStorage.removeItem(STORAGE_KEY);
    }
    if (linkedBusinessId) setAppliedUrlBusinessId(linkedBusinessId);
  }, [
    isBusinessesError,
    isBusinessesLoading,
    linkedBusinessId,
    user,
    isTenantUser,
    selectedBusinessId,
    storedBusinessId,
  ]);

  useEffect(() => {
    if (
      !user ||
      !urlBusinessId ||
      isBusinessesLoading ||
      isBusinessesError ||
      linkedBusinessId ||
      urlBusinessId === businessId
    )
      return;
    void setUrlBusinessId(businessId);
    if (!businessId) return;
    toast(
      `That link is for a business you don't have access to. Showing ${
        selectedBusiness?.name ?? "your business"
      } instead.`,
    );
  }, [
    user,
    urlBusinessId,
    businessId,
    isBusinessesLoading,
    isBusinessesError,
    linkedBusinessId,
    selectedBusiness?.name,
    setUrlBusinessId,
  ]);

  // Sync URL business ID with selected business ID for tenant users
  useEffect(() => {
    if (!isTenantUser || !businessId || isBusinessesLoading) return;

    const pathMatch = location.pathname.match(/\/business-settings\/([^/]+)/);
    if (pathMatch && pathMatch[1] !== businessId) {
      selectBusiness(businessId);
    }
  }, [
    location.pathname,
    businessId,
    isTenantUser,
    isBusinessesLoading,
    selectBusiness,
  ]);

  return (
    <BusinessContext.Provider
      value={{
        businesses,
        selectedBusinessId,
        businessId,
        isLoading: isUserLoading || isBusinessesLoading,
        isError: isUserError || isBusinessesError,
        selectBusiness,
        isTenantUser,
        selectedBusiness,
      }}
    >
      {children}
    </BusinessContext.Provider>
  );
};

export default BusinessProvider;
